/**
 * Generate NPS poll choices (0-10 scale with min/max labels)
 *
 * SOLID Principle: Single Responsibility - only generates choice structure
 * Does not handle API calls, state management, or UI rendering
 */

import { IChoice, IChoiceFormValues } from "@Interfaces";

/**
 * Generates 11 choices for an NPS poll (0-10 scale)
 * Labels are hard-coded to follow NPS standard
 *
 * NOTE: Backend validates exactly 11 choices
 *
 * @returns Array of IChoiceFormValues with:
 *   - 0: "0: Colder / Less favorable"
 *   - 1-9: "1" through "9"
 *   - 10: "10: Warmer / More favorable"
 */
export const generateNPSChoices = (): IChoiceFormValues[] => {
    const choices: IChoiceFormValues[] = [];

    for (let i = 0; i <= 10; i++) {
        let label = String(i);

        // Add hard-coded labels to minimum (0) and maximum (10)
        if (i === 0) {
            label = "0: Colder / Less favorable";
        } else if (i === 10) {
            label = "10: Warmer / More favorable";
        }

        choices.push({
            txt: label,
            img: null,
        });
    }

    return choices;
};

/**
 * Extract min label from NPS choices
 * Reverse operation of generateNPSChoices
 *
 * @param choices - Array of choices from an NPS poll
 * @returns Min label string, or "0" if not found
 */
export const extractMinLabel = (choices: IChoice[]): string => {
    if (!choices || choices.length === 0) return "0";

    const minChoice = choices[0];
    if (!minChoice?.txt) return "0";

    // Format: "0: Label" or just "0"
    const parts = minChoice.txt.split(": ");
    return parts.length > 1 ? parts[1] : "0";
};

/**
 * Extract max label from NPS choices
 * Reverse operation of generateNPSChoices
 *
 * @param choices - Array of choices from an NPS poll
 * @returns Max label string, or "9" if not found
 */
export const extractMaxLabel = (choices: IChoice[]): string => {
    if (!choices || choices.length === 0) return "9";

    const maxChoice = choices[choices.length - 1];
    if (!maxChoice?.txt) return "9";

    // Format: "9: Label" or just "9"
    const parts = maxChoice.txt.split(": ");
    return parts.length > 1 ? parts[1] : "9";
};
